<template>
  <b-card>
    <b-form @submit.prevent="handleSubmit" autocomplete="off">
      <!-- Section title like the mockup -->
      <div class="d-flex align-items-center mb-3">
        <h5 class="mb-0 text-primary mr-2"><strong>PROJECT</strong></h5>
        <div class="flex-grow-1 border-top" />
      </div>

      <b-row>
        <!-- Project Type -->
        <b-col md="6">
          <b-form-group label="Project Type" label-for="project-type">
            <b-form-radio-group
              id="project-type"
              v-model="projectType"
              :options="projectTypeOptions"
              name="projectTypeRadios"
              class="d-flex align-items-center"
              :inline="true"
            >
            </b-form-radio-group>
          </b-form-group>
        </b-col>

        <!-- Existing project selector -->
        <b-col md="6">
          <b-form-group
            label="Select Existing Project *"
            label-for="existing-project"
          >
            <b-form-select
              id="existing-project"
              :options="existingProjectOptions"
              v-model="form.existing_project_id"
              :disabled="isNewProject"
              :required="!isNewProject"
              @change="handleExistingChange"
              plain
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Existing project: read-only info (as a table) -->
      <div v-if="!isNewProject" class="mt-1">
        <div class="d-flex align-items-center mb-2">
          <h6 class="mb-0 text-muted mr-2"><strong>Project Info</strong></h6>
          <div class="flex-grow-1 border-top" />
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-striped mb-0">
            <tbody>
              <!-- Row: Project Name -->
              <tr>
                <td class="p-3" style="width: 30%">Project Name</td>
                <td class="p-3" style="width: 5%">:</td>
                <td class="p-3 font-weight-bold" style="width: 65%">{{
                  selectedProject?.project?.project_name || '-'
                }}</td>
              </tr>
              <!-- Row: Sub Project Name -->
              <tr>
                <td class="p-3">Sub Project Name</td>
                <td class="p-3">:</td>
                <td class="p-3 font-weight-bold">{{
                  selectedProject?.name || '-'
                }}</td>
              </tr>
              <!-- Row: Location -->
              <tr>
                <td class="p-3">Location</td>
                <td class="p-3">:</td>
                <td class="p-3 font-weight-bold">{{
                  selectedProject?.project?.location || '-'
                }}</td>
              </tr>
              <!-- Row: Status -->
              <tr>
                <td class="p-3">Status</td>
                <td class="p-3">:</td>
                <td class="p-3 font-weight-bold text-capitalize">{{
                  selectedProject?.project?.active ? 'Active' : 'Inactive'
                }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- New project: input fields -->
      <div v-else class="mt-1">
        <b-row>
          <b-col md="6">
            <b-form-group label="Project Name *" label-for="project-name">
              <b-form-input
                id="project-name"
                v-model.trim="form.project_name"
                placeholder="Project Name"
                required
                plain
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Sub Project Name *"
              label-for="sub-project-name"
            >
              <b-form-input
                id="sub-project-name"
                v-model.trim="form.sub_project_name"
                placeholder="Sub Project Name"
                required
                plain
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Project Location *"
              label-for="project-location"
            >
              <b-form-input
                id="project-location"
                v-model.trim="form.location"
                placeholder="Location"
                required
                plain
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Ship to Ship" label-for="sts">
              <div>
                <b-form-checkbox v-model="form.ship_to_ship" switch>
                  <template v-if="form.ship_to_ship">Yes</template>
                  <template v-else>No</template>
                </b-form-checkbox>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col cols="12">
          <hr />
          <div class="d-flex justify-content-between">
            <b-button variant="outline-secondary" @click="$emit('close')"
              >Cancel & Close</b-button
            >
            <b-button type="submit" variant="primary">Submit</b-button>
          </div>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
// @ts-nocheck
import { busdevActions, fleetsActions } from '@/src/Utils/helper'

export default {
  name: 'FormAddRunningShipment',
  props: {
    // optional: allow parent to pass preloaded projects
    projectsProp: {
      type: Array,
      default: () => [],
    },
    selectedTask: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      projectType: 'existing',
      form: {
        existing_project_id: null,
        // new project fields
        project_name: '',
        sub_project_name: '',
        location: '',
        ship_to_ship: false,
        // legacy payments removed; using structured single section values below
      },
      payments: {
        request_name: '',
        request_date: '',
        real_pricing: 0,
        invoice_price: 0,
        use_taxable: false,
        tax_name: '',
        tax_percentage: 0,
      },
      existingProjectOptions: [],
      selectedProject: null,
      planRows: [],
      fleetOptions: []
    }
  },
  computed: {
    isNewProject() {
      return this.projectType === 'new'
    },
    projectTypeOptions() {
      return [
        { text: 'Existing Project', value: 'existing' },
        { text: 'New Project', value: 'new' },
      ]
    },
    balance1() {
      // following mock, balance mirrors negative real pricing initially
      return 0 - Number(this.payments.real_pricing || 0)
    },
    balance2() {
      // balance after invoice
      return 0 - Number(this.payments.invoice_price || 0)
    },
    totalTax() {
      if (!this.payments.use_taxable) return 0
      const base = Number(this.payments.real_pricing || 0)
      const pct = Number(this.payments.tax_percentage || 0)
      return Math.round((base * pct) / 100)
    }
  },
  methods: {
    ...busdevActions,
    ...fleetsActions,
    formatIDR(val) {
      const n = Number(val || 0)
      return n.toLocaleString('id-ID')
    },
    async getProjectList() {
      const res = await this.fetchProjects()
      if (res.data.status == true) {
        if (res.data.data && res.data.data.length > 0) {
          res.data.data.forEach((val) => {
            this.existingProjectOptions.push({
              ...val,
              text: `${val.project?.project_name || ''} - ${val?.name}`,
              value: val.id,
            })
          })
          const existingProject = this.existingProjectOptions.find(
            (proj) => proj.value === this.selectedTask?.project_sub?.id
          )
          if (existingProject) {
            this.selectedProject = existingProject
            this.form.existing_project_id = existingProject.value
          }
        }
      }
    },
    handleExistingChange(val) {
      this.selectedProject = this.existingProjectOptions.find(
        (proj) => proj.value === val
      )
    },
    handleSubmit() {
      if (this.isNewProject) {
        if (
          !this.form.project_name ||
          !this.form.sub_project_name ||
          !this.form.location
        ) {
          this.$bvToast &&
            this.$bvToast.toast(
              'Please complete required fields for New Project.',
              {
                title: 'Validation',
                variant: 'warning',
                solid: true,
              }
            )
          return
        }
        this.$emit('submit', {
          mode: 'new',
          data: {
            project_name: this.form.project_name,
            sub_project_name: this.form.sub_project_name,
            location: this.form.location,
            ship_to_ship: this.form.ship_to_ship,
            selectedTask: this.selectedTask,
            payments: this.payments
          },
        })
      } else {
        if (!this.form.existing_project_id) {
          this.$bvToast &&
            this.$bvToast.toast('Please select an existing project.', {
              title: 'Validation',
              variant: 'warning',
              solid: true,
            })
          return
        }
        this.$emit('submit', {
          mode: 'existing',
          data: {
            project_id: this.form.existing_project_id,
            selectedTask: this.selectedTask,
            payments: this.payments
          },
        })
      }
    },
    async fetchFleets() {
      let params = {
        page: 1,
        perPage: 9999,
        showAll: true,
        active: true
      }

      let res = await this.getFleets(params)

      if (res.status) {
        if (res.data && res.data.length > 0) {
          this.fleetOptions = res.data.map((val) => ({
            ...val,
            text: val?.name,
            value: val.id,
            vehicle_type_id: val.vehicleType.id,
            checked: false,
          }))
        }
      }
    },
  },
  async mounted() {
    this.getProjectList()
    await this.fetchFleets()
    // map selectedTask.project_contract_plan into table rows
    const plans = this.selectedTask?.project_contract_plan || []
    this.planRows = plans.map((p) => ({
      checked: false,
      shipment_plan_detail_number: p.shipment_plan_detail_number,
      fleet_ownership: p.fleet_ownership,
      fleet_type: this.fleetOptions.find((f) => f.id === p.fleets?.[0]?.vehicle_id)?.vehicleType?.name,
      fleet_name: this.fleetOptions.find((f) => f.id === p.fleets?.[0]?.vehicle_id)?.name,
      cargo_type: p.cargo_type?.name,
      plan_quantity: p.plan_quantity,
      acceptance_date: p.acceptance_date
    }))
  },
}
</script>

<style scoped>
.info-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f1f3f5;
  border-radius: 6px;
  padding: 10px 12px;
}
.info-line .value {
  font-weight: 600;
}

/* Running Shipment table header styling to match mock */
.rs-table thead th {
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb !important;
  color: #111827;
  font-weight: 700;
}
.rs-header-top th {
  vertical-align: middle;
  text-align: center;
  font-size: 15px;
}
.rs-header-bottom th {
  font-size: 18px;
}
.rs-col-check { width: 40px; }
.rs-col-number { width: 260px; }
.rs-col-fleet-group { text-align: center; }
.rs-col-cargo { text-align: center; }

.rs-table tbody td {
  font-size: 18px;
}
</style>
